import { toast as sonnerToast, Toaster as SonnerToaster } from 'sonner';
import { cva } from 'class-variance-authority';
import Icon from '@uconnect/assets/components/ui/icon';
import { Button } from './button';

export type ToasterProps = React.ComponentProps<typeof SonnerToaster>;

export interface ToastProps {
	id: string | number;
	title: string;
	description?: string;
	type?: 'default' | 'success' | 'error' | 'warning' | 'info';
}

type ToastInput = string | Omit<ToastProps, 'id'>;

const TYPE_ICONS = {
	success: 'check',
	error: 'triangleExclamation',
	warning: 'circleExclamation',
	info: 'circleQuestion',
	default: null,
} as const;

// Uneven padding to account for whitespace from the close icon
const toastVariants = cva(
	'sonner-toast flex items-center gap-3 w-full! rounded-md shadow-lg text-sm! pl-4! py-3! pr-3!',
	{
		variants: {
			type: {
				default: 'bg-white! text-foreground',
				success: 'bg-green-50! text-green-800',
				error:   'bg-red-50! text-red-800',
				warning: 'bg-yellow-50! text-yellow-800',
				info:    'bg-blue-50! text-blue-800',
			},
		},
		defaultVariants: {
			type: 'default',
		},
	}
);

export function Toast( { id, title, description, type = 'default' }: ToastProps ) {
	const iconName = TYPE_ICONS[ type ];

	return (
		<div className={ toastVariants( { type } ) }>
			{ iconName && (
				<Icon
					name={ iconName }
					className="sonner-toast__icon mt-0.5 shrink-0 size-4 block!"
				/>
			) }

			<div className="flex-1">
				<p className="sonner-toast__title mb-0!">{ title }</p>
				{ description && (
					<p className="sonner-toast__description text-xs opacity-80 mt-0.5 mb-0!">
						{ description }
					</p>
				) }
			</div>

			<Button
				variant="mutedInterface"
				aria-label="Dismiss notification"
				className="sonner-toast__close ml-auto shrink-0 self-start p-1! h-auto! w-auto! text-current! hover:text-current! block! leading-none!"
				onClick={ () => sonnerToast.dismiss( id ) }
			>
				<Icon name="xmark" className="block!" />
			</Button>
		</div>
	);
}

function makeToast( input: ToastInput, type: ToastProps[ 'type' ] = 'default' ) {
	const props =
		typeof input === 'string'
			? { title: input, type }
			: { type, ...input };

	return sonnerToast.custom( ( id ) => <Toast id={ id } { ...props } /> );
}

function toast( input: ToastInput ) {
	return makeToast( input, 'default' );
}

toast.success = ( input: ToastInput ) => makeToast( input, 'success' );
toast.error   = ( input: ToastInput ) => makeToast( input, 'error' );
toast.warning = ( input: ToastInput ) => makeToast( input, 'warning' );
toast.info    = ( input: ToastInput ) => makeToast( input, 'info' );
toast.dismiss = sonnerToast.dismiss;

export { toast };

export function Toaster( { ...props }: ToasterProps ) {
	// eslint-disable-next-line react/jsx-props-no-spreading
	return <SonnerToaster { ...props } />;
}
